import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { loadAllData, getDataForTimeIndex} from './utils/dataLoader';
import labelsNames from './data/labels_names.json';
import VideoPlayer from './VideoPlayer';
import SpeedSlider from './SpeedSlider';
import CollapsibleChat from './CollapsibleChat';

import surgicalVideo from './data/c80/video41/video41_short.mp4';

const actionColors = [
    "bg-red-200", "bg-blue-200", "bg-green-200", "bg-yellow-200",
    "bg-purple-200", "bg-pink-200", "bg-indigo-200", "bg-gray-200"
];

const ProgressBar = ({ progress }) => (
    <div className="w-full bg-gray-200 rounded-full h-2.5 dark:bg-gray-700">
      <div 
        className="bg-blue-600 h-2.5 rounded-full" 
        style={{ width: `${progress}%` }}
      ></div>
    </div>
);

const ActionCard = ({ action, time, isElapsed = false, color }) => (
    <div className="p-4 rounded-lg bg-white shadow-md">
        <div className="flex justify-between items-center mb-2">
            <h3 className="text-lg font-semibold">{action}</h3>
            <span className="text-xs text-gray-500">
                {isElapsed ? 'Elapsed' : 'Remaining'}: {time !== Infinity ? `${time.toFixed(2)} min` : 'Not predicted'}
            </span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-2">
            <div 
                className={`h-2 rounded-full ${color}`}
                style={{ 
                    width: `${time !== Infinity ? Math.min((time / 18) * 100, 100) : 0}%`,
                    transition: 'width 0.5s ease-in-out'
                }}
            ></div>
        </div>
    </div>
);

const CompressingTimeline = ({ actions, totalWidth = 100, maxSegments = 50 }) => {
    const segments = actions.slice(-maxSegments);
    const segmentWidth = totalWidth / segments.length;
    const uniqueActions = Array.from(new Set(segments.map(action => action.action)));

    return (
        <div className="mb-4">
            <div className="flex w-full h-8 bg-gray-200 rounded-lg overflow-hidden">
                {segments.map((action, index) => (
                    <div
                        key={index}
                        className={`${action.color}`}
                        style={{ width: `${segmentWidth}%` }}
                    ></div>
                ))}
            </div>
            <div className="flex justify-between text-xs mt-1 mb-2">
                <span>Start</span>
                <span>Now</span>
            </div>
            <div className="flex flex-wrap gap-2 justify-center">
                {uniqueActions.map((action, index) => (
                    <div key={index} className="flex items-center">
                        <div className={`w-4 h-4 ${actionColors[labelsNames.indexOf(action) % actionColors.length]} rounded-full mr-1`}></div>
                        <span className="text-xs">{action}</span>
                    </div>
                ))}
            </div>
        </div>
    );
};



const SurgicalActionPredictor = ({ videoId }) => {
  const [timeIndex, setTimeIndex] = useState(0);
  const [currentClass, setCurrentClass] = useState('');
  const [futureClasses, setFutureClasses] = useState([]);
  const [remainingTimes, setRemainingTimes] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [error, setError] = useState(null);
  const [previousActions, setPreviousActions] = useState([]);
  const [currentActionElapsedTime, setCurrentActionElapsedTime] = useState(0);
  const [videoUrl, setVideoUrl] = useState('');
  const [speed, setSpeed] = useState(1);
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [predictionData, setPredictionData] = useState(null);
  const [videoDuration, setVideoDuration] = useState(300); // Default to 5 minutes (300 seconds)
  const predictionUpdateInterval = 30; // Update every x second (video is at 1fps)

  const getUpcomingActions = useCallback((futureClasses, remainingTimes) => {
    const actionTimeMap = new Map();
    labelsNames.forEach((action, index) => {
      const time = remainingTimes[index];
      actionTimeMap.set(action, time !== undefined ? time : Infinity);
    });

    return Array.from(actionTimeMap.entries())
      .sort((a, b) => a[1] - b[1])
      .filter(([action, time]) => action !== currentClass && time < Infinity)
      .map(([action, time]) => ({ name: action, predictedTime: time }));
  }, [currentClass]);

  const updateData = useCallback((index) => {
    console.log(`Updating data for time index: ${index}`);
    const data = getDataForTimeIndex(index);
    if (data) {
      console.log('Data retrieved:', data);
      if (data.predCurrentClass !== currentClass) {
        setCurrentClass(data.predCurrentClass);
        setCurrentActionElapsedTime(0);
      }
      setFutureClasses(data.predFutureClasses);
      setRemainingTimes(data.predRemainingTimes);

      setPreviousActions(prev => {
        const actionIndex = labelsNames.indexOf(data.predCurrentClass);
        const color = actionColors[actionIndex % actionColors.length];
        const newPrev = [...prev, { action: data.predCurrentClass, color }];
        return newPrev.slice(-50);
      });

      setPredictionData({
        currentAction: data.predCurrentClass,
        elapsedTime: index,
        upcomingActions: getUpcomingActions(data.predFutureClasses, data.predRemainingTimes)
      });
    } else {
      console.error('No data retrieved for time index:', index);
    }
  }, [currentClass, getUpcomingActions]);

  useEffect(() => {
    const initializeData = async () => {
      setIsLoading(true);
      setError(null);
      try {
        console.log(`Initializing data for video ${videoId}...`);
        const { videoLength } = await loadAllData(videoId, setLoadingProgress);
        setVideoDuration(videoLength);
        setIsLoading(false);
        updateData(0);
        setVideoUrl(surgicalVideo);
      } catch (err) {
        console.error('Failed to load data:', err);
        setError(`Failed to load data: ${err.message}`);
        setIsLoading(false);
      }
    };

    initializeData();
  }, [videoId, updateData]);


  useEffect(() => {
    if (!isLoading && videoDuration > 0) {
      const timer = setInterval(() => {
        setTimeIndex(prevIndex => {
          const newIndex = (prevIndex + 1) % videoDuration;
          if (newIndex % predictionUpdateInterval === 0) {
            updateData(newIndex);
          }
          return newIndex;
        });
    
        setCurrentActionElapsedTime(prevTime => prevTime + 1 / (predictionUpdateInterval * speed));
      }, 1000 / speed);
    
      return () => clearInterval(timer);
    }
  }, [isLoading, speed, updateData, predictionUpdateInterval, videoDuration]);

  const handleSpeedChange = (newSpeed) => {
      setSpeed(newSpeed);
  };
  
  const renderTimeline = useMemo(() => (
    <div className="w-full h-8 bg-gray-200 rounded-lg overflow-hidden flex">
      {[currentClass, ...futureClasses].slice(0, 18).map((action, index) => (
        <div
          key={index}
          className={`h-full ${actionColors[labelsNames.indexOf(action) % actionColors.length]}`}
          style={{ width: `${100 / 18}%` }}
        >
          {index === 0 && (
            <div
              className="h-full bg-green-500"
              style={{ width: `${(60 - (timeIndex % 60)) / 60 * 100}%` }}
            ></div>
          )}
        </div>
      ))}
    </div>
  ), [currentClass, futureClasses, timeIndex]);

  const renderLegend = useMemo(() => (
    <div className="flex flex-wrap justify-center gap-2 mt-2">
      {Array.from(new Set([currentClass, ...futureClasses])).map((action, index) => (
        <div key={index} className="flex items-center">
          <div className={`w-4 h-4 ${actionColors[labelsNames.indexOf(action) % actionColors.length]} rounded-full mr-1`}></div>
          <span className="text-xs">{action}</span>
        </div>
      ))}
    </div>
  ), [currentClass, futureClasses]);

  const handleVideoTimeUpdate = (currentTime) => {
      const newTimeIndex = Math.floor(currentTime);
      setTimeIndex(newTimeIndex);
      if (newTimeIndex % predictionUpdateInterval === 0) {
        updateData(newTimeIndex);
      }
      setCurrentActionElapsedTime(currentTime % 60);
  };

  if (isLoading) {
      return (
        <div className="flex flex-col items-center justify-center h-screen">
          <h2 className="text-2xl font-bold mb-4">Loading Predictions from Surgical Video</h2>
          <ProgressBar progress={loadingProgress} />
          <p className="mt-2">{loadingProgress}% Loaded</p>
        </div>
      );
  }

  if (error) {
      return (
        <div className="flex flex-col items-center justify-center h-screen">
          <h2 className="text-2xl font-bold mb-4 text-red-600">Error</h2>
          <p className="text-center">{error}</p>
        </div>
      );
  }

  return (
    <div className="flex h-screen overflow-hidden">
      <div className={`transition-all duration-300 ease-in-out ${isChatOpen ? 'w-1/2' : 'w-full'} h-screen overflow-y-auto`}>
        <div className="max-w-4xl mx-auto p-4">
          {/* Video Player */}
          <div className="w-full mb-6">
            <VideoPlayer
              videoUrl={videoUrl}
              currentTime={timeIndex}
              playbackRate={speed}
              onTimeUpdate={handleVideoTimeUpdate}
            />
          </div>

          {/* Timeline */}
          <div className="w-full mb-6">
            <p className="text-lg font-semibold mb-2">Upcoming Phases</p>
            {renderTimeline}
            <div className="flex justify-between text-xs mt-1">
              <span>Now</span>
              <span>Future</span>
            </div>
            {renderLegend}
          </div>

          {/* Actions Container */}
          <div className="w-full flex flex-col items-center space-y-6">
            {/* Previous Actions */}
            <div className="w-full">
              <p className="text-lg font-semibold mb-2">Previous Phases:</p>
              <CompressingTimeline actions={previousActions} maxSegments={50} />
            </div>

            {/* Current Action */}
            <div className="w-full">
              <p className="text-lg font-semibold mb-2">Current Phase:</p>
              {currentClass && (
                <ActionCard
                  action={currentClass}
                  time={currentActionElapsedTime}
                  isElapsed={true}
                  color={actionColors[0]}
                />
              )}
            </div>

            {/* Upcoming Actions */}
            <div className="w-full">
              <p className="text-lg font-semibold mb-2">Upcoming Phases:</p>
              <div className="space-y-4">
                {getUpcomingActions(futureClasses, remainingTimes).map((action, index) => (
                  <ActionCard
                    key={index}
                    action={action.name}
                    time={action.predictedTime}
                    color={actionColors[(index + 1) % actionColors.length]}
                  />
                ))}
              </div>
            </div>
          </div>

          {/* Speed Control */}
          <div className="w-full mt-6">
            <p className="text-sm font-medium text-gray-700 mb-2">
              Playback Speed: {speed}x
            </p>
            <SpeedSlider speed={speed} onChange={handleSpeedChange} />
          </div>

          {/* Elapsed Time */}
          <p className="mt-4 text-sm text-gray-600">
            Elapsed Time: {timeIndex} seconds
          </p>
        </div>
      </div>

      {/* Chat toggle button (only visible when chat is closed) */}
      {!isChatOpen && (
        <button
          onClick={() => setIsChatOpen(true)}
          className="fixed right-4 top-4 bg-blue-500 text-white p-2 rounded-full hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
          </svg>
        </button>
      )}

      {/* Collapsible Chat */}
      {predictionData && (
        <div className={`transition-all duration-300 ease-in-out ${isChatOpen ? 'w-1/2' : 'w-0'} h-screen`}>
          <CollapsibleChat
            predictionData={predictionData}
            isOpen={isChatOpen}
            onClose={() => setIsChatOpen(false)}
          />
        </div>
      )}
    </div>
  );
};

export default SurgicalActionPredictor;